import FileSaver from "file-saver";
import moment from "moment";
import Notification from "@source/components/snackMessage/notification";
import ReqError from "@source/api/reqError";

const REPORT_MONTHS = ["Jan", "Feb", "March", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface ConsentReportQuery {
  nursingHomeIds?: string[];
  totals?: boolean;
  categoryIds?: string[];
  statuses?: string[];
  awaiting?: string[];
  sentFrom?: string;
  sentTo?: string;
  signedFrom?: string;
  signedTo?: string;
  signerTypes?: string[];
  signingMethods?: string[];
  dueWithinDays?: number;
}

export interface ConsentReportFile {
  bytes: ArrayBuffer;
  fileName: string;
}

export type ConsentReportParams = Record<string, string | number | boolean | string[]>;

const listKeys: Array<keyof ConsentReportQuery> = [
  "nursingHomeIds",
  "categoryIds",
  "statuses",
  "awaiting",
  "signerTypes",
  "signingMethods",
];

const dateKeys: Array<keyof ConsentReportQuery> = ["sentFrom", "sentTo", "signedFrom", "signedTo"];

export const compactReportQuery = (query: ConsentReportQuery): ConsentReportParams => {
  const params: ConsentReportParams = {};
  listKeys.forEach((key) => {
    const value = query[key];
    if (Array.isArray(value) && value.length) params[key] = value;
  });
  dateKeys.forEach((key) => {
    const value = query[key];
    if (typeof value === "string" && value) params[key] = value;
  });
  if (query.totals) params.totals = true;
  if (query.dueWithinDays !== undefined) params.dueWithinDays = query.dueWithinDays;
  return params;
};

export const parseDueWithinDays = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
};

export const toReportDayRange = (range: [Date, Date] | [Date] | null): { from?: string; to?: string } => {
  if (!range?.[0]) return {};
  return {
    from: moment(range[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    to: range[1] ? moment(range[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSZ") : undefined,
  };
};

export const consentReportFallbackName = (prefix: string, date = new Date()) =>
  `${prefix}-${date.getDate()}_${REPORT_MONTHS[date.getMonth()]}_${date.getFullYear()}.xlsx`;

export const fileNameFromContentDisposition = (header: string | undefined, fallback: string) => {
  if (!header) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  const plain = /filename="?([^";]+)"?/i.exec(header)?.[1];
  const raw = encoded || plain;
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

export const saveConsentReportFile = (bytes: ArrayBuffer, fileName: string) => {
  const blob = new Blob([bytes], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
  FileSaver.saveAs(blob, fileName);
};

export const notifyConsentReportError = (error: unknown) => {
  if (error instanceof ReqError && (error.status === 502 || error.status === 503 || error.status === 504)) return;
  const message = error instanceof Error && error.message ? error.message : "Unable to download the report.";
  new Notification().error(message);
};
